/**
 * Links that open Paige from a web page or an email: `capturepoint://tour`,
 * `capturepoint://job/lookups` and the rest of the allowlist below (docs/product/app-links.md).
 *
 * A link is outside input. Nothing in it is ever used as a file path, job id, command or
 * markup: the text after the scheme is only compared against this fixed list, and the
 * answer is one of these names. A link that names anything else still opens Paige
 * and does nothing more. Percent-encoding is never decoded, so `%74our` is not `tour`.
 *
 * Self-contained (no imports) so the main process, the preload and the node tests can all
 * use it as it is.
 */

/** The scheme registered with Windows for an installed build. */
export const appLinkScheme = 'capturepoint';

/**
 * The places in a job a link can open. A link never names the job: the main window picks it
 * (the person's one job, or it asks which), so nothing in a link can point at a job.
 */
export const jobAppLinkRoutes = [
  'job/learning/results',
  'job/learning/train',
  'job/documents/teach',
  'job/lookups',
  'job/thresholds',
] as const;
export type JobAppLinkRoute = (typeof jobAppLinkRoutes)[number];

/** Every route a link can name. `open` only brings Paige forward. */
export const appLinkRoutes = [
  'open',
  'tour',
  'settings/license',
  'settings/performance',
  ...jobAppLinkRoutes,
] as const;
export type AppLinkRoute = (typeof appLinkRoutes)[number];

/** Longer than any real link; anything longer is not read at all, it just opens the app. */
export const appLinkMaxLength = 2048;

const prefix = `${appLinkScheme}:`;

export const isAppLinkRoute = (value: unknown): value is AppLinkRoute =>
  typeof value === 'string' && (appLinkRoutes as readonly string[]).includes(value);

export const isJobAppLinkRoute = (value: unknown): value is JobAppLinkRoute =>
  typeof value === 'string' && (jobAppLinkRoutes as readonly string[]).includes(value);

const isAppLink = (value: string) =>
  value.trimStart().slice(0, prefix.length).toLowerCase() === prefix;

/**
 * The route a single command-line value names, or null when it is not a Paige
 * link. Case, a leading `//`, a trailing slash, a query string and a fragment do not
 * matter (browsers and Windows add the slash: `capturepoint://tour/`). Every other
 * Paige link is `open`.
 */
export const parseAppLink = (value: unknown): AppLinkRoute | null => {
  if (typeof value !== 'string' || !isAppLink(value)) return null;
  if (value.length > appLinkMaxLength) return 'open';
  const rest = value.trim().slice(prefix.length);
  const end = rest.search(/[?#]/);
  const route = (end === -1 ? rest : rest.slice(0, end))
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .toLowerCase();
  if (route === '') return 'open';
  return isAppLinkRoute(route) ? route : 'open';
};

/**
 * The link on a command line (a first launch's `process.argv`, or the `argv` a second
 * launch hands the running one), or null when it carries none. The first link wins;
 * Windows passes one.
 */
export const findAppLink = (argv: readonly unknown[]): AppLinkRoute | null => {
  for (const value of argv) {
    const route = parseAppLink(value);
    if (route) return route;
  }
  return null;
};

/**
 * Only the Velopack-installed Windows build registers the scheme. A development run, or a
 * packaged build run from build output by a test harness, would point every link on the
 * computer at an executable that is about to go away.
 */
export const registersAppLinks = (build: {
  readonly isPackaged: boolean;
  readonly isInstalled: boolean;
  readonly platform: string;
}) => build.isPackaged && build.isInstalled && build.platform === 'win32';
